import { lazy } from 'react'
import { isMiniappTabType } from '@papan/types'
import type { EditorRegistration } from '../editor-registry.js'

/**
 * The PDApp editor — a settings/run surface for the app principal (access
 * grants, triggers, the live preview).
 *
 * DELIBERATELY NOT in the extension registry. Every other editor is dispatched
 * by extension alone, and this one cannot be, because `.pdapp` names TWO things
 * (v1 format freeze §3a): the `.pdapp/` FOLDER is the editable source this
 * editor edits, while a `.pdapp` FILE is the sealed, signed release zip. The
 * registry has no concept of folder-ness, so a key here resolves for both.
 *
 * That is not hypothetical. While these aliases WERE registered, the shared map
 * handed the app editor to every consumer of it — `SingleFileView` (`/file/:id`),
 * `VersionPreview` (which would then render the LIVE app as a historical
 * version), and `TabContent`'s `.shared` pointer arm (which would also drop the
 * pointer's forced read-only, since this registration has no `readOnly`). Each
 * would have needed its own folder check, and the next consumer added would
 * have needed to remember one — the same drift that made issue #365.
 *
 * So the folder test lives at the ONE dispatch site that can make it
 * (`TabContent`, which reads `isFolder` from file detail), and everywhere else
 * an app suffix stays unregistered and falls back exactly as it did before #365.
 * `editor-registry-coverage.test.ts` asserts the aliases are absent from the map.
 */
export const APP_FOLDER_EDITOR: EditorRegistration = {
  family: 'app',
  extensions: [],
  name: 'PDApp',
  component: lazy(() =>
    import('../../ui/MiniAppEditor.js').then(m => ({ default: m.MiniAppEditor }))
  ),
}

/**
 * The dispatch rule the registry cannot express: an app SUFFIX opens the PDApp
 * editor only when the row is actually a FOLDER.
 *
 * There are TWO surfaces that mount editors for app folders — `TabContent`
 * (workspace tabs) and `SingleFileView` (`/file/:id`, MobileShell, and the
 * chat-home side panel, which `ChatHomeView.handleOpenPath` routes app folders
 * into deliberately). One shared rule rather than two copies, because two copies
 * of a folder check that must agree is the drift that produced #365 in the first
 * place.
 *
 * `isFolder` is deliberately tri-state. `undefined`/`null` mean NOT RESOLVED and
 * return null, so a caller that has not loaded metadata yet renders its loading
 * state instead of momentarily mounting the app editor against a file id.
 */
export function resolveAppFolderEditor(
  ext: string,
  isFolder: boolean | null | undefined,
): EditorRegistration | null {
  if (!isMiniappTabType(ext)) return null
  return isFolder === true ? APP_FOLDER_EDITOR : null
}
